import { Add, Delete, Edit, Quiz } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import {
  IWorkshopEvaluation,
  IWorkshopEvaluationInput,
} from "models/workshopEvaluation";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import {
  WorkshopEvaluationCells,
  addWorkshopEvaluation,
  deleteWorkshopEvaluation,
  editWorkshopEvaluation,
  getWorkshopEvaluation,
} from "framework/workshopEvaluation";
import AddWorkshopEvaluation from "./partials/AddWorkshopEvaluation/AddWorkshopEvaluation";
import BlockIcon from "@mui/icons-material/Block";
import EditWorkshopEvaluation from "./partials/EditWorkshopEvaluation/EditWorkshopEvaluation";
import DeleteWorkshopEvaluation from "./partials/DeleteWorkshopEvaluation/DeleteWorkshopEvaluation";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { IPermission } from "models/permissions";
import { getPermission } from "helpers";
import { Link } from "react-router-dom";

interface Props {
  feature: Feature;
}

const WorkshopEvaluation: React.FC<Props> = ({ feature }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [loading3, setLoading3] = useState<boolean>(true);
  const { t, i18n } = useTranslation();
  const [permission, setPermission] = useState<IPermission | null>(null);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterWorkshopEvaluation, setFilterWorkshopEvaluation] = useState<
    IWorkshopEvaluation[]
  >([] as IWorkshopEvaluation[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedWorkshopEvaluation, setSelectedWorkshopEvaluation] =
    useState<IWorkshopEvaluation>({} as IWorkshopEvaluation);
  const [inputObj, setInputObj] = useState<IWorkshopEvaluationInput>(
    {} as IWorkshopEvaluationInput
  );
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addIndustriesMutation = useMutation({
    mutationFn: (createInput: IWorkshopEvaluationInput) => {
      return addWorkshopEvaluation(createInput);
    },
  });

  const editIndustriesMutation = useMutation({
    mutationFn: (data: {
      createInput: IWorkshopEvaluationInput;
      id: string;
    }) => {
      return editWorkshopEvaluation(data.createInput, data.id);
    },
  });

  const deleteIndustriesMutation = useMutation({
    mutationFn: (data: { id: string }) => {
      return deleteWorkshopEvaluation(data.id);
    },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let response: any = await queryClient.fetchQuery(
        "get workshop evaluations",
        () => getWorkshopEvaluation()
      );
      response =
        response && response.length > 0
          ? response.map((item: IWorkshopEvaluation) => ({
            ...item,
          }))
          : [];
      setFilterWorkshopEvaluation(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    setLoading3(true);
    try {
      const res = await getPermission(30);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
    fetchPermission();
    // eslint-disable-next-line
  }, []);

  if (loading || loading3)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if (name == "isActive" && value == null) {
      value = false;
    }
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () =>{setInputObj({} as IWorkshopEvaluationInput);setOpenAdd(false);} 

  const handleOpenEdit = (WorkshopEvaluation: IWorkshopEvaluation) => {
    setSelectedWorkshopEvaluation(WorkshopEvaluation);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setInputObj({} as IWorkshopEvaluationInput)
    setSelectedWorkshopEvaluation({} as IWorkshopEvaluation);
  };

  const handleOpenDelete = (WorkshopEvaluation: IWorkshopEvaluation) => {
    setSelectedWorkshopEvaluation(WorkshopEvaluation);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedWorkshopEvaluation({} as IWorkshopEvaluation);
  };

  const getBodyCells = (row: IWorkshopEvaluation) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "title")
        return {
          id: "title",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.title}</Typography>
            </TableCell>
          ),
        };

      if (key === "titleAr")
        return {
          id: "titleAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.titleAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "isActive")
        return {
          id: "isActive",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.isActive ? "Yes" : "No"}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "createdOn")
        return {
          id: "createdOn",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {new Date(row.createdOn).toLocaleDateString()}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "timeToComplete")
        return {
          id: "timeToComplete",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {Number(row.timeToComplete).toFixed(1) + ' ' + t('T_minute')}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"right"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                justifyContent={i18n.language === "en" ? "start" : "end"}
                spacing={1}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      color={"error"}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_questions")}>
                    <Link to={`/workshopEvaluationQuestions/${row.id}`}>
                      <IconButton size={"small"} color={"info"}>
                        <Quiz />
                      </IconButton>
                    </Link>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.title  || !inputObj.TimeToComplete)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addIndustriesMutation.mutateAsync({ ...inputObj, isActive: inputObj?.isActive || false, TimeToComplete: +inputObj.TimeToComplete });
      setAlert({
        open: true,
        message: "WorkshopEvaluation added successfully",
        type: "success",
      });
      fetchData();
      handleCloseAdd();
      setInputObj({} as IWorkshopEvaluationInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      let createInput: IWorkshopEvaluationInput = {
        title: inputObj.title || selectedWorkshopEvaluation.title,
        titleAr: inputObj.titleAr === null ? "":inputObj.titleAr || selectedWorkshopEvaluation.titleAr,
        isActive: inputObj.isActive ?? selectedWorkshopEvaluation.isActive,
        TimeToComplete: +inputObj.TimeToComplete || +selectedWorkshopEvaluation.timeToComplete,
      };
      await editIndustriesMutation.mutateAsync({
        createInput,
        id: selectedWorkshopEvaluation.id,
      });
      setAlert({
        open: true,
        message: "WorkshopEvaluation updated successfully",
        type: "success",
      });
      fetchData();
      handleCloseEdit();
      setInputObj({} as IWorkshopEvaluationInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteIndustriesMutation.mutateAsync({
        id: selectedWorkshopEvaluation.id,
      });
      setAlert({
        open: true,
        message: "WorkshopEvaluation deleted successfully",
        type: "success",
      });
      fetchData();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography variant="h3" color={"header.primary"}>
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add_workshop") + ' ' + t('T_evaluation')}>
                <IconButton
                  sx={{
                    // bgcolor: 'primary.main',
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.light",
                      color: "white",
                    },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! &&
          FilterWorkshopEvaluation.length > 0 && (
            <List
              headCells={WorkshopEvaluationCells}
              data={FilterWorkshopEvaluation}
              getBodyCells={getBodyCells}
              feature={feature}
              canEdit={false}
            />
          )}
        {(!FilterWorkshopEvaluation || FilterWorkshopEvaluation.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_industry_header")}
              message={t("T_no_industry_message")}
            />
          )}
        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddWorkshopEvaluation
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditWorkshopEvaluation
          open={openEdit}
          loading={loading2}
          workshopEvaluation={selectedWorkshopEvaluation}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteWorkshopEvaluation
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default WorkshopEvaluation;
